'use client'

import React, { useEffect, useRef, useState, useMemo } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../helpers/index.js'

// 定义样式变体
const countdownVariants = cva(
  // 基础样式：倒计时容器
  'countdown-container flex w-full items-center',
  {
    variants: {
      variant: {
        outline: 'gap-1',
        spacious: 'gap-1',
      },
      align: {
        left: 'justify-start',
        center: 'justify-center',
      },
    },
    defaultVariants: {
      variant: 'outline',
      align: 'left',
    },
  }
)

const timeBlockVariants = cva(
  // 基础样式：时间块
  'time-block border-box rounded-box-small flex flex-col items-center justify-center overflow-hidden text-center text-xs',
  {
    variants: {
      variant: {
        outline:
          'border-info-primary text-info-primary desktop:size-12 desktop:p-1 size-10 border bg-transparent p-[2px]',
        spacious: 'bg-info-white text-info-primary desktop:size-12 desktop:p-1 size-10 p-[2px]',
      },
    },
    defaultVariants: {
      variant: 'outline',
    },
  }
)

const timeNumberVariants = cva(
  'time-number lg-desktop:text-[24px] translate-y-[2px] whitespace-nowrap text-center text-[20px] font-bold leading-none'
)

const separatorVariants = cva(
  // 基础样式：分隔符
  'separator desktop:text-2xl text-xl font-bold',
  {
    variants: {
      variant: {
        outline: 'text-info-primary',
        spacious: 'text-info-primary',
      },
    },
    defaultVariants: {
      variant: 'outline',
    },
  }
)

/**
 * 主题色覆盖类 - 仅当 theme 传入时覆盖 variant 的颜色部分，保留布局/尺寸
 */
const getTimeBlockThemeClasses = (theme?: 'light' | 'dark'): string => {
  if (!theme) return ''
  return theme === 'light'
    ? 'bg-info-primary text-info-white border-transparent'
    : 'bg-container-secondary text-info-primary border-transparent'
}

const getSeparatorThemeClasses = (theme?: 'light' | 'dark'): string => {
  if (!theme) return ''
  return 'text-info-primary'
}

/**
 * 时间标签配置接口 (singular keys)
 */
export interface TimeLabels {
  /** 天的标签 */
  day: string
  /** 小时的标签 */
  hour: string
  /** 分钟的标签 */
  minute: string
  /** 秒的标签 */
  second: string
}

/**
 * 时间标签配置接口 (plural keys, e.g. from Title component)
 */
export interface PluralTimeLabels {
  days: string
  hours: string
  minutes: string
  seconds: string
}

/**
 * 时间标签输入类型 - 支持 singular 和 plural 键名，所有字段可选
 */
export type TimeLabelsInput = Partial<TimeLabels & PluralTimeLabels>

/**
 * 倒计时组件Props接口
 */
export interface CountdownProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onExpire'>, VariantProps<typeof countdownVariants> {
  /** 结束时间 - ISO格式字符串 */
  endDate: string
  /** 结束时间时区，如: America/Los_Angeles */
  endDate_tz?: string
  /** 格式化用的locale，默认使用navigator.language */
  locale?: string
  /** 时间标签配置，支持 singular (day/hour/minute/second) 和 plural (days/hours/minutes/seconds) 键名 */
  timeLabels?: TimeLabelsInput
  /** JSON字符串格式的时间标签（向后兼容） */
  dateFormat?: string
  /** 倒计时结束回调 */
  onExpire?: () => void
  /** 分隔符字符，默认为 ':' */
  separator?: string
  /** 隐藏已过期的倒计时，默认为 true */
  hideWhenExpired?: boolean
  /** 是否显示天数，默认为 true */
  showDays?: boolean
  /** 是否显示小时，默认为 true */
  showHours?: boolean
  /** 是否显示分钟，默认为 true */
  showMinutes?: boolean
  /** 是否显示秒数，默认为 true */
  showSeconds?: boolean
  /** 颜色主题 - 覆盖 variant 的颜色部分，保留布局/尺寸。不传则保持 variant 原有颜色 */
  theme?: 'light' | 'dark'
}

/**
 * 数字补零函数
 */
const pad = (n: number, len = 2) => String(Math.abs(n)).padStart(len, '0')

/**
 * 默认时间标签
 */
const getDefaultTimeLabels = (): TimeLabels => ({
  day: 'Day',
  hour: 'Hours',
  minute: 'Mins',
  second: 'Secs',
})

/**
 * 将 plural/singular 混合标签规范化为 singular 格式
 * singular 键优先于 plural 键，缺失时使用默认值
 */
const resolveTimeLabels = (labels: TimeLabelsInput): TimeLabels => {
  const defaults = getDefaultTimeLabels()
  return {
    day: labels.day ?? labels.days ?? defaults.day,
    hour: labels.hour ?? labels.hours ?? defaults.hour,
    minute: labels.minute ?? labels.minutes ?? defaults.minute,
    second: labels.second ?? labels.seconds ?? defaults.second,
  }
}

/**
 * 解析dateFormat JSON字符串（向后兼容）
 */
const safeStringToObject = (str: string): TimeLabelsInput => {
  try {
    let jsonStr = str?.trim?.()
    if (!jsonStr?.startsWith?.('{') || !jsonStr?.endsWith?.('}')) {
      return {}
    }
    jsonStr = jsonStr?.replace?.(/(\w+)\s*:/g, '"$1":')
    return JSON.parse(jsonStr) as TimeLabelsInput
  } catch (err) {
    return {}
  }
}

/**
 * Countdown - 倒计时原子组件
 *
 * @description 用于显示倒计时的组件，支持天、时、分、秒显示，支持多种样式变体、主题和单位可见性控制
 */
const Countdown = React.forwardRef<HTMLDivElement, CountdownProps>(
  (
    {
      className,
      endDate,
      endDate_tz,
      locale,
      timeLabels,
      dateFormat,
      onExpire,
      separator = ':',
      hideWhenExpired = true,
      showDays = true,
      showHours = true,
      showMinutes = true,
      showSeconds = true,
      variant,
      align,
      theme,
      ...props
    },
    ref
  ) => {
    // 确定最终使用的时间标签 (支持 plural 和 singular 键)
    const finalTimeLabels = useMemo(() => {
      if (timeLabels) return resolveTimeLabels(timeLabels)
      if (dateFormat) return resolveTimeLabels(safeStringToObject(dateFormat))
      return getDefaultTimeLabels()
    }, [timeLabels, dateFormat])

    // 解析目标时间
    const targetMsRef = useRef<number>(Date.parse(endDate))
    const [remainingMs, setRemainingMs] = useState<number>(() => {
      const t = targetMsRef.current
      return isNaN(t) ? 0 : Math.max(0, t - Date.now())
    })

    // 判断是否已过期
    const [isExpired, setIsExpired] = useState<boolean>(() => {
      const t = targetMsRef.current
      return !isNaN(t) && t <= Date.now()
    })

    // 当endDate改变时更新目标时间
    useEffect(() => {
      targetMsRef.current = Date.parse(endDate)
      const t = targetMsRef.current
      const newRemaining = isNaN(t) ? 0 : Math.max(0, t - Date.now())
      const newIsExpired = !isNaN(t) && t <= Date.now()

      setRemainingMs(newRemaining)
      setIsExpired(newIsExpired)
    }, [endDate])

    // 启动倒计时定时器
    useEffect(() => {
      if (isExpired) return // 已过期则不启动
      if (isNaN(targetMsRef.current)) return // 无效日期不启动

      let expiredCalled = false
      const tick = () => {
        const now = Date.now()
        const remaining = Math.max(0, targetMsRef.current - now)
        setRemainingMs(remaining)

        if (remaining <= 0 && !expiredCalled) {
          expiredCalled = true
          setIsExpired(true)
          onExpire?.()
        }
      }

      tick() // 立即执行一次
      const intervalId = window.setInterval(tick, 1000)

      return () => clearInterval(intervalId)
    }, [onExpire, isExpired, endDate])

    // 计算时间单位
    const totalSeconds = Math.floor(remainingMs / 1000)
    const seconds = totalSeconds % 60
    const totalMinutes = Math.floor(totalSeconds / 60)
    const minutes = totalMinutes % 60
    const totalHours = Math.floor(totalMinutes / 60)
    const hours = totalHours % 24
    const days = Math.floor(totalHours / 24)

    // 构建可见的时间单位数组
    const units: Array<{ value: number; label: string; cssClass: string }> = []
    if (showDays) units.push({ value: days, label: finalTimeLabels.day, cssClass: 'time-days-box' })
    if (showHours) units.push({ value: hours, label: finalTimeLabels.hour, cssClass: 'time-hours-box' })
    if (showMinutes) units.push({ value: minutes, label: finalTimeLabels.minute, cssClass: 'time-minutes-box' })
    if (showSeconds) units.push({ value: seconds, label: finalTimeLabels.second, cssClass: 'time-seconds-box' })

    // 构建动态 aria-label
    const ariaLabelParts: string[] = []
    if (showDays) ariaLabelParts.push(`${days} days`)
    if (showHours) ariaLabelParts.push(`${pad(hours)} hours`)
    if (showMinutes) ariaLabelParts.push(`${pad(minutes)} minutes`)
    if (showSeconds) ariaLabelParts.push(`${pad(seconds)} seconds`)
    const ariaLabel = ariaLabelParts.length > 0 ? `${ariaLabelParts.join(' ')} remaining` : 'countdown timer'

    // 如果已过期且需要隐藏，仅保留 screen reader 通知
    if (isExpired && hideWhenExpired) {
      return (
        <span role="status" aria-live="polite" className="sr-only">
          Countdown has ended
        </span>
      )
    }

    const timeBlockTheme = getTimeBlockThemeClasses(theme)
    const separatorTheme = getSeparatorThemeClasses(theme)

    return (
      <div
        ref={ref}
        className={cn(countdownVariants({ variant, align }), className)}
        role="timer"
        aria-label={ariaLabel}
        {...props}
      >
        {units.map((unit, index) => (
          <React.Fragment key={unit.cssClass}>
            <div className={cn(timeBlockVariants({ variant }), timeBlockTheme, unit.cssClass)}>
              <div className={timeNumberVariants()}>{pad(unit.value, 2)}</div>
              <div className="truncate text-center text-[12px] font-bold">{unit.label}</div>
            </div>
            {index < units.length - 1 && (
              <div className={cn(separatorVariants({ variant }), separatorTheme)}>{separator}</div>
            )}
          </React.Fragment>
        ))}
        {/* Visually hidden status for screen readers - announces only on expiry */}
        {isExpired && (
          <span role="status" aria-live="polite" className="sr-only">
            Countdown has ended
          </span>
        )}
      </div>
    )
  }
)

Countdown.displayName = 'Countdown'

export default Countdown
